import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Usuarios from "./pages/Usuarios";
import Postos from "./pages/Postos";
import Feriados from "./pages/Feriados";
import Funcionarios from "./pages/Funcionarios";
import Rotas from "./pages/Rotas";
import FazerRota from "./pages/FazerRota";
import HistoricoRotas from "./pages/HistoricoRotas";
import FechamentoMensal from "./pages/FechamentoMensal";
import HistoricoPonto from "./pages/HistoricoPonto";
import BaterPonto from "./pages/BaterPonto";
import MeuHistorico from "./pages/MeuHistorico";
import MeusDados from "./pages/MeusDados";
import Aprovacoes from "./pages/Aprovacoes";
import Parametros from "./pages/Parametros";
import Afastamentos from "./pages/Afastamentos";
import AtrasosFaltas from "./pages/AtrasosFaltas";
import Auditoria from "./pages/Auditoria";
import Ajuda from "./pages/Ajuda";
import Empresas from "./pages/Empresas";
import Comprovante from "./pages/Comprovante";
import Privacidade from "./pages/Privacidade";
import ExclusaoDados from "./pages/ExclusaoDados";
import AppLayout from "./layout/AppLayout";
import AdminLayout from "./layout/AdminLayout";
import AdminClientes from "./pages/admin/Clientes";
import AdminUsuarios from "./pages/admin/Usuarios";
import AdminBackup from "./pages/admin/Backup";
import AdminMeusDados from "./pages/admin/MeusDados";

function RequireAuth({ perfil, children }) {
  const { token, usuario } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  if (perfil && !perfil.includes(usuario?.perfil)) {
    return <Navigate to={usuario?.perfil === "admin" ? "/admin" : "/"} replace />;
  }
  return children;
}

// Admin da Empresa não tem cadastro de funcionário nem bate ponto — é um
// perfil só de configuração. Sem isso ele cairia na tela de Bater Ponto e
// veria "seu login não está vinculado", uma mensagem sem sentido pra esse perfil.
// Gestor cai direto no Dashboard (visão geral da equipe) — Bater Ponto virou
// uma tela própria em /bater-ponto, dentro da aba "Meu Ponto" da sidebar.
// Funcionário não tem Dashboard, então continua caindo direto em Bater Ponto.
function Home() {
  const { usuario } = useAuth();
  if (usuario?.perfil === "gestor" || usuario?.perfil === "admin_empresa") return <Navigate to="/dashboard" replace />;
  return <BaterPonto />;
}

export default function App() {
  return (
    <AuthProvider>
      {/* basename vazio hoje (raiz) — quando o sistema migrar pra /app (ver
          plano da landing page), o build de produção vai rodar com
          PUBLIC_URL=/app, e este mesmo código passa a navegar certinho sob
          esse prefixo sem precisar mudar nada aqui de novo. */}
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/exclusao-de-dados" element={<ExclusaoDados />} />

          <Route
            path="/admin"
            element={
              <RequireAuth perfil={["admin"]}>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="clientes" replace />} />
            <Route path="clientes" element={<AdminClientes />} />
            <Route path="usuarios" element={<AdminUsuarios />} />
            <Route path="backup" element={<AdminBackup />} />
            <Route path="meus-dados" element={<AdminMeusDados />} />
          </Route>

          <Route
            path="/"
            element={
              <RequireAuth perfil={["gestor", "funcionario", "admin_empresa"]}>
                <AppLayout />
              </RequireAuth>
            }
          >
            {/* Página inicial pros dois perfis operacionais — funcionário só
                faz isso mesmo, e o gestor pediu pra já cair direto batendo o
                próprio ponto em vez de ver o Dashboard primeiro. Dashboard
                virou uma tela própria em /dashboard. Admin da Empresa não
                bate ponto (perfil só de configuração) — cai direto em
                Funcionários, que é sua tela principal. */}
            <Route index element={<Home />} />
            <Route path="bater-ponto" element={<BaterPonto />} />
            <Route
              path="dashboard"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Dashboard /></RequireAuth>}
            />
            <Route path="meu-historico" element={<MeuHistorico />} />
            <Route path="comprovante/:id" element={<Comprovante />} />
            <Route path="meus-dados" element={<MeusDados />} />
            <Route path="ajuda" element={<Ajuda />} />
            <Route
              path="hora-extra"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Aprovacoes /></RequireAuth>}
            />
            <Route
              path="parametros"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Parametros /></RequireAuth>}
            />
            <Route
              path="atrasos-faltas"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><AtrasosFaltas /></RequireAuth>}
            />
            <Route
              path="afastamentos"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Afastamentos /></RequireAuth>}
            />
            <Route
              path="postos"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Postos /></RequireAuth>}
            />
            <Route
              path="feriados"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Feriados /></RequireAuth>}
            />
            <Route
              path="rotas"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Rotas /></RequireAuth>}
            />
            <Route
              path="fazer-rota"
              element={<RequireAuth perfil={["gestor", "funcionario", "admin_empresa"]}><FazerRota /></RequireAuth>}
            />
            <Route
              path="historico-rotas"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><HistoricoRotas /></RequireAuth>}
            />
            <Route
              path="funcionarios"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Funcionarios /></RequireAuth>}
            />
            <Route
              path="funcionarios/historico"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><HistoricoPonto /></RequireAuth>}
            />
            <Route
              path="relatorios"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><FechamentoMensal /></RequireAuth>}
            />
            <Route
              path="usuarios"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Usuarios /></RequireAuth>}
            />
            <Route
              path="empresas"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Empresas /></RequireAuth>}
            />
            <Route
              path="auditoria"
              element={<RequireAuth perfil={["gestor", "admin_empresa"]}><Auditoria /></RequireAuth>}
            />
          </Route>

          {/* Sem isso, qualquer rota que não bate com nada acima (link
              quebrado, digitação errada) renderiza tela branca — nenhuma das
              <Routes> aninhadas dá match e React Router não mostra nada. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
